/**
 * Resume Intelligence pre-processes resume data.
 * Performs bullet sanitization, empty data scrubs, date formats alignment,
 * and skill list categorization mapping before rendering.
 * Resolves legacy/V2 schema mismatches.
 */

export function runResumeIntelligence(rawData, options = {}) {
  const data = JSON.parse(JSON.stringify(rawData)); // Deep clone to avoid mutating input state
  const activeSections = [];

  // 1. Personal Info check & normalization (support both flat and nested schemas)
  if (!data.personalInfo) {
    data.personalInfo = {};
  }
  
  // Extract flat fallback fields if present
  data.personalInfo.name = data.personalInfo.name || data.name || 'Your Name';
  data.personalInfo.roleTitle = data.personalInfo.roleTitle || data.personalInfo.role || data.role || data.roleTitle || '';
  data.personalInfo.email = data.personalInfo.email || data.email || '';
  data.personalInfo.phone = data.personalInfo.phone || data.phone || '';
  data.personalInfo.location = data.personalInfo.location || data.personalInfo.city || data.city || data.location || '';
  
  if (!data.personalInfo.links) {
    data.personalInfo.links = {};
  }
  data.personalInfo.links.linkedin = data.personalInfo.links.linkedin || data.personalInfo.linkedin || data.linkedin || '';
  data.personalInfo.links.github = data.personalInfo.links.github || data.personalInfo.github || data.github || '';
  const portfolioUrl = data.personalInfo.links.portfolio || data.personalInfo.portfolio || data.personalInfo.website || data.portfolio || data.website || '';
  data.personalInfo.links.portfolio = portfolioUrl;

  if (hasData(data.personalInfo)) {
    activeSections.push('personal-info');
  }

  // 2. Summary normalization
  const summaryText = data.summary || data.summaryText || '';
  data.summary = summaryText;
  if (summaryText) {
    activeSections.push('summary');
  }

  // 3. Experience normalization
  if (data.experience && data.experience.length > 0) {
    activeSections.push('experience');
    data.experience = data.experience.map(job => {
      // Resolve startDate and endDate fallback to duration
      let start = job.startDate ? formatNormalizedDate(job.startDate) : '';
      let end = job.endDate ? formatNormalizedDate(job.endDate) : '';
      
      if (!start && job.duration) {
        start = job.duration;
      }
      
      const bList = (job.bullets || job.points || []).map(b => cleanBulletPoint(b));
      return {
        ...job,
        role: job.role || 'Professional Role',
        company: job.company || job.organization || 'Organization',
        location: job.location || '',
        startDate: start,
        endDate: end,
        bullets: bList,
        points: bList
      };
    });
  }

  // 4. Skills normalization and grouping
  if (data.skills && data.skills.length > 0) {
    activeSections.push('skills');
    
    // Group skills: prefer user-defined categories, fall back to auto-categorization
    if (options.skillsComponent === 'categorized') {
      if (Array.isArray(data.skillCategories) && data.skillCategories.length > 0) {
        const grouped = {};
        data.skillCategories.forEach(cat => {
          if (cat && cat.name && Array.isArray(cat.skills) && cat.skills.length > 0) {
            grouped[cat.name] = cat.skills;
          }
        });
        if (Object.keys(grouped).length > 0) {
          data.skillsCategories = grouped;
        } else {
          data.skillsCategories = autoCategorizeSkills(data.skills);
        }
      } else {
        data.skillsCategories = autoCategorizeSkills(data.skills);
      }
    }
  }

  // 5. Projects normalization
  if (data.projects && data.projects.length > 0) {
    activeSections.push('projects');
    data.projects = data.projects.map(proj => {
      const bList = (proj.bullets || proj.points || []).map(b => cleanBulletPoint(b));
      return {
        ...proj,
        name: proj.name || proj.title || 'Project Name',
        techStack: proj.techStack || proj.tech || proj.technologies,
        duration: proj.duration || proj.date || '',
        bullets: bList,
        points: bList
      };
    });
  }

  // 6. Education normalization
  if (data.education && data.education.length > 0) {
    activeSections.push('education');
    data.education = data.education.map(edu => ({
      ...edu,
      school: edu.school || edu.university || edu.college || '',
      degree: edu.degree || edu.degreeName || edu.course || '',
      cgpa: edu.cgpa || edu.gpa || '',
      gpa: edu.gpa || edu.cgpa || '',
      duration: edu.duration || edu.date || edu.year || 'Expected May 2026'
    }));
  }

  // 7. Internships normalization
  if (data.internships && data.internships.length > 0) {
    activeSections.push('internships');
    data.internships = data.internships.map(intern => {
      let start = intern.startDate ? formatNormalizedDate(intern.startDate) : '';
      let end = intern.endDate ? formatNormalizedDate(intern.endDate) : '';
      if (!start && intern.duration) {
        start = intern.duration;
      }
      const bList = (intern.bullets || intern.points || []).map(b => cleanBulletPoint(b));
      return {
        ...intern,
        role: intern.role || 'Intern',
        company: intern.company || intern.organization || 'Organization',
        location: intern.location || '',
        startDate: start,
        endDate: end,
        bullets: bList,
        points: bList
      };
    });
  }

  // 8. Other secondary sections
  const otherSections = [
    'certifications', 'languages', 'achievements', 'extracurricular',
    'publications', 'patents', 'conferences', 'licenses', 'awards', 'courses',
    'memberships', 'volunteer', 'speaking', 'interests', 'hobbies', 'references',
    'socials'
  ];
  otherSections.forEach(secName => {
    if (data[secName] && data[secName].length > 0) {
      activeSections.push(secName);
    }
  });

  // Custom sections
  Object.keys(data).forEach(key => {
    if (key.startsWith('custom') && data[key] && (data[key].title || data[key].content)) {
      activeSections.push(key);
    }
  });

  // If V2 schema sections list is present, check enabled & visible settings
  if (Array.isArray(data.sections)) {
    data.sections.forEach(secId => {
      const settings = data.sectionSettings?.[secId];
      const normalizedId = secId === 'contact' ? 'personal-info' : secId;
      if (settings?.enabled !== false && settings?.visible !== false) {
        if (!activeSections.includes(normalizedId)) {
          activeSections.push(normalizedId);
        }
      }
    });
  }

  return { processedData: data, activeSections };
}

function hasData(obj) {
  if (!obj || typeof obj !== 'object') return false;
  return Object.values(obj).some(val => val !== null && val !== undefined && val !== '');
}

function cleanBulletPoint(bulletText) {
  if (!bulletText) return '';
  return bulletText
    .trim()
    .replace(/^[\s•\-*]+/g, '') // Strip preceding bullet marks
    .replace(/\s+/g, ' '); // Normalize spaces
}

function formatNormalizedDate(dateStr) {
  if (!dateStr) return '';
  // Convert standard date string formats: e.g. "2023-01-01" to "Jan 2023"
  const date = new Date(dateStr);
  if (isNaN(date.getTime())) return dateStr; // Return as-is if parsing fails
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${months[date.getMonth()]} ${date.getFullYear()}`;
}

function autoCategorizeSkills(skillsList) {
  // Hardcoded rule map for classifying tech stacks
  const taxonomy = {
    'Languages': ['javascript', 'python', 'java', 'typescript', 'c++', 'ruby', 'go', 'php', 'sql'],
    'Frontend': ['react', 'next.js', 'vue', 'angular', 'css', 'tailwind', 'html', 'sass'],
    'Backend': ['spring boot', 'django', 'express', 'node.js', 'flask', 'nest.js', 'laravel'],
    'Cloud & Devops': ['aws', 'docker', 'kubernetes', 'ci/cd', 'github actions', 'gcp', 'azure'],
  };

  const categories = {};
  const unclassified = [];

  skillsList.forEach(skillObj => {
    const name = skillObj.name || (typeof skillObj === 'string' ? skillObj : '');
    if (!name) return;
    const lowerName = name.toLowerCase();
    let categorized = false;

    for (const [catName, keywords] of Object.entries(taxonomy)) {
      if (keywords.some(k => lowerName.includes(k))) {
        if (!categories[catName]) categories[catName] = [];
        categories[catName].push(name);
        categorized = true;
        break;
      }
    }

    if (!categorized) {
      unclassified.push(name);
    }
  });

  if (unclassified.length > 0) {
    categories['Other Stack'] = unclassified;
  }

  return categories;
}
